import { useEffect, useState } from 'react';
import { CaretDown, Check, X } from 'shared/icons';
import { useTokens } from './tokens';

export function TBtn({
    children,
    variant = 'default',
    size = 'md',
    onClick,
    style = {},
    disabled,
    title,
    icon,
    ...rest
}) {
    const T = useTokens();
    const pad = { xs: '2px 7px', sm: '4px 10px', md: '6px 14px', lg: '8px 18px' }[size] || '6px 14px';
    const fs = { xs: 11, sm: 12, md: 13, lg: 14 }[size] || 13;
    const radius = 4;
    const base = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: pad,
        fontSize: fs,
        fontFamily: T.sans,
        borderRadius: radius,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        transition: 'background 0.12s, opacity 0.12s',
        whiteSpace: 'nowrap',
        lineHeight: 1.4,
        outline: 'none',
    };
    const v = {
        default: { background: T.bg, color: T.ink, border: `1px solid ${T.line}` },
        primary: { background: T.ink, color: T.bg, border: `1px solid ${T.ink}` },
        ghost: { background: 'transparent', color: T.inkMid, border: '1px solid transparent' },
        accent: { background: T.accent, color: '#fff', border: `1px solid ${T.accent}` },
        danger: { background: T.dangerBg, color: T.danger, border: `1px solid ${T.dangerLine}` },
        soft: { background: T.bgMid, color: T.inkMid, border: `1px solid ${T.line}` },
    };
    return (
        // Ordem importa: variant sobrescreve base; style do caller sobrescreve
        // variant (assim quem passa style={{background:'#107C41'}} ganha sobre
        // os defaults da variant 'default').
        <button
            type="button"
            disabled={disabled}
            title={title}
            onClick={onClick}
            style={{ ...base, ...v[variant], ...style }}
            {...rest}
        >
            {icon && <span style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>}
            {children}
        </button>
    );
}

export function TChip({
    children,
    active,
    dot,
    removable,
    onRemove,
    onClick,
    dropdown,
    muted,
    style = {},
    color,
    title,
    ...rest
}) {
    const T = useTokens();
    const dotColor = color === 'warn' ? T.warn : color === 'ok' ? T.ok : T.accent;
    const chipStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        height: 28,
        padding: '0 10px',
        border: `1px solid ${active ? T.ink : T.line}`,
        background: active ? T.ink : T.bg,
        color: active ? T.bg : muted ? T.muted : T.inkMid,
        fontSize: 12,
        fontFamily: T.sans,
        borderRadius: 4,
        cursor: onClick ? 'pointer' : 'default',
        userSelect: 'none',
        flexShrink: 0,
        transition: 'border-color 0.1s, background 0.1s',
        outline: 'none',
        ...style,
    };
    const inner = (
        <>
            {dot && (
                <span
                    style={{
                        width: 6,
                        height: 6,
                        borderRadius: 99,
                        background: dot === true ? dotColor : dot,
                        flexShrink: 0,
                    }}
                />
            )}
            <span style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {children}
            </span>
            {dropdown && (
                <span style={{ opacity: 0.55, marginLeft: 1, display: 'inline-flex' }}>
                    <CaretDown size={11} />
                </span>
            )}
            {removable && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove?.();
                    }}
                    style={{
                        marginLeft: 2,
                        opacity: 0.5,
                        fontSize: 14,
                        lineHeight: 1,
                        cursor: 'pointer',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        color: 'inherit',
                        fontFamily: 'inherit',
                    }}
                >
                    ×
                </button>
            )}
        </>
    );
    if (onClick) {
        return (
            <button type="button" onClick={onClick} style={chipStyle} title={title} {...rest}>
                {inner}
            </button>
        );
    }
    return (
        <div style={chipStyle} title={title} {...rest}>
            {inner}
        </div>
    );
}

export function TInput({ placeholder, value, onChange, icon, width = 240, style = {}, autoFocus, ...rest }) {
    const T = useTokens();
    return (
        <div
            {...rest}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                border: `1px solid ${T.line}`,
                padding: '0 10px',
                height: 32,
                width,
                background: T.bg,
                borderRadius: 4,
                flexShrink: 0,
                ...style,
            }}
        >
            {icon && <span style={{ color: T.muted, fontSize: 14, flexShrink: 0, lineHeight: 1 }}>{icon}</span>}
            <input
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                placeholder={placeholder}
                // biome-ignore lint/a11y/noAutofocus: explicit prop from caller
                autoFocus={autoFocus}
                style={{
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontFamily: T.sans,
                    fontSize: 13,
                    color: T.ink,
                    flex: 1,
                    minWidth: 0,
                }}
            />
            {value && (
                <button
                    type="button"
                    onClick={() => onChange?.('')}
                    style={{
                        color: T.muted,
                        cursor: 'pointer',
                        fontSize: 14,
                        lineHeight: 1,
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        fontFamily: 'inherit',
                    }}
                >
                    ×
                </button>
            )}
        </div>
    );
}

export function TBadge({ children, color = 'accent' }) {
    const T = useTokens();
    // Paleta de cores únicas pra system tags + user tags = accent (azul).
    // 'orange' (baixo estoque) e 'info' (outbound) são distintas do 'warn'
    // (reputacao warning, amarelo) pra evitar confusão visual.
    const c = {
        accent: { bg: T.accentBg, color: T.accent },
        warn: { bg: T.warnBg, color: T.warn },
        ok: { bg: T.okBg, color: T.ok },
        muted: { bg: T.bgMid, color: T.muted },
        ink: { bg: T.inkMid, color: T.bg },
        danger: { bg: T.dangerBg, color: T.danger },
        // System tag colors — REALMENTE DISTINTAS entre si (sem 2 tags com
        // visual idêntico). Paleta:
        //   - danger (vermelho)   → 'sem estoque'
        //   - orange (laranja)    → 'baixo estoque'
        //   - warn   (amarelo)    → tags de aviso amarelas
        //   - purple (roxo)       → 'reputacao unhealthy' / 'reputacao warning'
        //                            (visualmente único — não conflita com
        //                            sem estoque/baixo estoque)
        //   - info   (ciano)      → 'outbound'
        //   - muted  (cinza)      → 'sem venda 30d'
        orange: { bg: 'rgba(255, 140, 50, 0.15)', color: 'rgb(255, 140, 50)' },
        info: { bg: 'rgba(60, 200, 220, 0.15)', color: 'rgb(60, 200, 220)' },
        purple: { bg: 'rgba(180, 110, 255, 0.18)', color: 'rgb(180, 110, 255)' },
        purpleDeep: { bg: 'rgba(200, 80, 240, 0.20)', color: 'rgb(220, 130, 255)' },
        //   - rose   (rosa)       → 'bling insuficiente' (Envio Full)
        rose: { bg: 'rgba(240, 90, 140, 0.16)', color: 'rgb(240, 90, 140)' },
    }[color] || { bg: T.accentBg, color: T.accent };
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '1px 6px',
                borderRadius: 3,
                fontSize: 11,
                fontFamily: T.sans,
                fontWeight: 500,
                background: c.bg,
                color: c.color,
                whiteSpace: 'nowrap',
            }}
        >
            {children}
        </span>
    );
}

export function Spark({ values, width = 70, height = 20, color }) {
    const T = useTokens();
    const c = color || T.accent;
    if (!values || values.length < 2) {
        return null;
    }
    const max = Math.max(...values, 1);
    const min = Math.min(...values, 0);
    const range = max - min || 1;
    const pts = values
        .map((v, i) => {
            const x = (i / (values.length - 1)) * width;
            const y = height - ((v - min) / range) * (height - 3) - 1.5;
            return `${x.toFixed(1)},${y.toFixed(1)}`;
        })
        .join(' ');
    return (
        <svg width={width} height={height} aria-hidden="true" style={{ display: 'block', overflow: 'visible' }}>
            <polyline
                points={pts}
                fill="none"
                stroke={c}
                strokeWidth="1.6"
                strokeLinejoin="round"
                strokeLinecap="round"
            />
        </svg>
    );
}

export function TKPI({ label, value, hint, color, mini, style = {} }) {
    const T = useTokens();
    const c = color === 'warn' ? T.warn : color === 'ok' ? T.ok : T.ink;
    return (
        <div
            style={{
                padding: mini ? '8px 12px' : '12px 16px',
                border: `1px solid ${T.line}`,
                background: T.bg,
                borderRadius: 4,
                ...style,
            }}
        >
            <div
                style={{
                    fontSize: 10,
                    fontFamily: T.sans,
                    color: T.muted,
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                    marginBottom: 4,
                }}
            >
                {label}
            </div>
            <div
                style={{
                    fontSize: mini ? 18 : 22,
                    fontFamily: T.mono,
                    fontWeight: 600,
                    color: c,
                    lineHeight: 1,
                }}
            >
                {value}
            </div>
            {hint && <div style={{ fontSize: 10, color: T.muted, marginTop: 3, fontFamily: T.sans }}>{hint}</div>}
        </div>
    );
}

export function TDivider({ vertical, style = {} }) {
    const T = useTokens();
    return (
        <div
            style={
                vertical
                    ? { width: 1, alignSelf: 'center', background: T.line, ...style }
                    : { height: 1, background: T.line, ...style }
            }
        />
    );
}

export function TDItem({ children, onClick, active, icon, hint, danger }) {
    const T = useTokens();
    const [hov, setHov] = useState(false);
    return (
        <button
            type="button"
            onClick={onClick}
            onMouseEnter={() => setHov(true)}
            onMouseLeave={() => setHov(false)}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 14px',
                cursor: 'pointer',
                background: hov ? T.bgSoft : active ? T.bgMid : 'transparent',
                color: danger ? T.danger : active ? T.ink : T.inkMid,
                fontSize: 13,
                fontFamily: T.sans,
                border: 'none',
                borderLeft: active ? `2px solid ${T.ink}` : '2px solid transparent',
                width: '100%',
                textAlign: 'left',
                outline: 'none',
            }}
        >
            {icon && (
                <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center', color: T.muted }}>{icon}</span>
            )}
            <span style={{ flex: 1 }}>{children}</span>
            {hint && <span style={{ fontSize: 11, color: T.muted }}>{hint}</span>}
            {active && <Check size={14} color={T.ink} />}
        </button>
    );
}

export function TModal({ open, onClose, children, width = 700, title, ...rest }) {
    const T = useTokens();
    useEffect(() => {
        if (!open) {
            return;
        }
        const handler = (e) => {
            if (e.key === 'Escape') {
                onClose?.();
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open, onClose]);
    if (!open) {
        return null;
    }
    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.65)',
                display: 'grid',
                placeItems: 'center',
                zIndex: 1000,
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    onClose?.();
                }
            }}
        >
            <div
                {...rest}
                style={{
                    width,
                    maxWidth: 'calc(100vw - 40px)',
                    maxHeight: 'calc(100vh - 80px)',
                    background: T.bg,
                    borderRadius: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
                    overflow: 'hidden',
                }}
            >
                {title && (
                    <div
                        style={{
                            padding: '16px 20px',
                            borderBottom: `1px solid ${T.line}`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                        }}
                    >
                        <div style={{ fontSize: 15, fontWeight: 600, color: T.ink, flex: 1 }}>{title}</div>
                        <TBtn variant="ghost" size="sm" onClick={onClose}>
                            <X size={14} />
                        </TBtn>
                    </div>
                )}
                <div style={{ flex: 1, overflow: 'auto' }}>{children}</div>
            </div>
        </div>
    );
}

export function useClickOutside(ref, handler, active = true) {
    useEffect(() => {
        if (!active) {
            return;
        }
        const h = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                handler();
            }
        };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, [ref, handler, active]);
}
